import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import { Dialog } from './accounts/AccountBits';
import nurseryService from '../../services/nursery';
import { errorMessage } from '../../utils/apiResponse';
import type { RetailCategory } from '../../types/nursery';

/**
 * The shop's own categories (Fruit trees, Natives, Potting mix…), set on each
 * stock item for reports and the counter. Separate from tree type, which says
 * how a plant is grown and sold. Retired, never deleted: an item keeps the
 * category it had.
 */
export function RetailCategoriesDialog({ onClose, onChanged }: {
  onClose: () => void;
  /** Called after any change, so the page can reload what it shows. */
  onChanged?: () => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'RetailCategoriesDialog' });
  const [categories, setCategories] = useState<RetailCategory[] | null>(null);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await nurseryService.getRetailCategories(true);
      if (res.success) setCategories(res.data || []);
      else setError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    }
  };

  useEffect(() => { void load(); }, []);

  const run = async (action: () => Promise<{ success: boolean }>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await action();
      if (!res.success) { setError(errorMessage(res, t('saveFailed'))); return false; }
      await load();
      onChanged?.();
      return true;
    } catch (e) {
      setError(errorMessage(e, t('saveFailed')));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    if (await run(() => nurseryService.createRetailCategory({ name }))) setNewName('');
  };

  const rename = async () => {
    if (!editing || !editing.name.trim()) return;
    if (await run(() => nurseryService.updateRetailCategory(editing.id, { name: editing.name.trim() }))) setEditing(null);
  };

  return (
    <Dialog
      title={t('title')}
      subtitle={t('subtitle')}
      onClose={onClose}
      footer={<Button variant="outline" onClick={onClose}>{t('done')}</Button>}
    >
      <form
        className="flex flex-col sm:flex-row gap-2"
        onSubmit={(e) => { e.preventDefault(); void add(); }}
      >
        <label htmlFor="new-retail-category" className="sr-only">{t('newCategory')}</label>
        <input
          id="new-retail-category"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={100}
          placeholder={t('placeholder')}
          className="flex-1 min-w-0 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <Button type="submit" variant="primary" size="sm" disabled={busy || !newName.trim()}>{t('add')}</Button>
      </form>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {categories == null ? (
        <p className="text-sm text-neutral-400">{t('loading')}</p>
      ) : categories.length === 0 ? (
        <p className="text-sm text-neutral-500">{t('none')}</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
          {categories.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
              {editing?.id === c.id ? (
                <>
                  <label htmlFor={`category-${c.id}`} className="sr-only">{t('categoryName')}</label>
                  <input
                    id={`category-${c.id}`}
                    value={editing.name}
                    onChange={(e) => setEditing({ id: c.id, name: e.target.value })}
                    maxLength={100}
                    className="flex-1 min-w-0 border border-neutral-300 rounded-md px-2 py-1 text-sm"
                  />
                  <Button size="sm" variant="primary" disabled={busy} onClick={() => void rename()}>{t('save')}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>{t('cancel')}</Button>
                </>
              ) : (
                <>
                  <span className={c.active ? 'flex-1 min-w-0 break-words text-sm text-neutral-800' : 'flex-1 min-w-0 break-words text-sm text-neutral-400 line-through'}>
                    {c.name}
                  </span>
                  <span className="text-xs text-neutral-500">
                    {t('items', { count: c.item_count ?? 0 })}
                  </span>
                  {c.active && (
                    <button type="button" className="text-xs text-primary-600 font-medium" onClick={() => setEditing({ id: c.id, name: c.name })}>
                      {t('rename')}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    className="text-xs text-neutral-600 font-medium"
                    onClick={() => void run(() => nurseryService.updateRetailCategory(c.id, { active: !c.active }))}
                  >
                    {c.active ? t('retire') : t('bringBack')}
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
